import React, { useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { AlertOctagon, AlertTriangle, ChevronDown, Clock, Code2, Eye, ShieldAlert, ShieldCheck, Sparkles, Wrench } from 'lucide-react';
import { SecurityFinding } from '@/types';

interface SecurityFindingAccordionProps {
  finding: SecurityFinding;
  index: number;
  expandedPanel: string | false;
  onAccordionChange: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  onAssignTask: (findingId: string) => void;
  isTaskDisabled?: boolean;
}

export function SecurityFindingAccordion({
  finding,
  index,
  expandedPanel,
  onAccordionChange,
  onAssignTask,
  isTaskDisabled = false,
}: SecurityFindingAccordionProps) {
  const theme = useTheme();

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#22c55e';
      case 'info': return '#6366f1';
      default: return theme.palette.grey[500];
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertOctagon size={16} color={getSeverityColor(severity)} />;
      case 'high': return <ShieldAlert size={16} color={getSeverityColor(severity)} />;
      case 'medium': return <AlertTriangle size={16} color={getSeverityColor(severity)} />;
      case 'low': return <ShieldCheck size={16} color={getSeverityColor(severity)} />;
      default: return <ShieldCheck size={16} color={getSeverityColor(severity)} />;
    }
  };

  const getTypeLabel = (type?: string) => {
    switch (type) {
      case 'vulnerability': return 'Vulnerability';
      case 'weakness': return 'Weakness';
      case 'misconfiguration': return 'Misconfiguration';
      case 'hotspot': return 'Hotspot';
      default: return null;
    }
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'vulnerability': return '#ef4444';
      case 'weakness': return '#f59e0b';
      case 'misconfiguration': return '#f97316';
      case 'hotspot': return '#8b5cf6';
      default: return theme.palette.grey[500];
    }
  };

  const formatEffort = (minutes?: number) => {
    if (!minutes) return null;
    if (minutes < 60) return `~${minutes}min`;
    const hours = Math.round(minutes / 60 * 10) / 10;
    return `~${hours}h`;
  };

  const [showAllEvidence, setShowAllEvidence] = useState(false);

  const EVIDENCE_CAP = 10;
  const panelId = `finding-${index}`;
  const isExpanded = expandedPanel === panelId;
  const sevColor = getSeverityColor(finding.severity);
  const allRecs = finding.recommendation
    ? [finding.recommendation, ...(finding.recommendations || [])]
    : (finding.recommendations || []);
  const dedupedRecs = [...new Set(allRecs)];

  return (
    <Accordion
      expanded={isExpanded}
      onChange={onAccordionChange(panelId)}
      elevation={0}
      disableGutters
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        border: '1px solid',
        borderColor: isExpanded ? alpha(sevColor, 0.3) : 'divider',
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        mb: 1,
        overflow: 'hidden',
        transition: 'border-color 0.2s',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          minHeight: 56,
          px: 2,
          '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
          bgcolor: isExpanded ? alpha(sevColor, 0.03) : 'transparent',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, mr: 1 }}>
          {getSeverityIcon(finding.severity)}
          <Typography sx={{ fontSize: 13, fontWeight: 500, flex: 1 }}>
            {finding.title || finding.finding}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip
              label={finding.severity.toUpperCase()}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 600,
                bgcolor: alpha(sevColor, 0.1),
                color: sevColor,
                borderRadius: 1,
              }}
            />
            {finding.type && getTypeLabel(finding.type) && (
              <Chip
                label={getTypeLabel(finding.type)}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 500,
                  bgcolor: alpha(getTypeColor(finding.type), 0.08),
                  color: getTypeColor(finding.type),
                  borderRadius: 1,
                }}
              />
            )}
            <Chip
              label={finding.category}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 500,
                textTransform: 'capitalize',
                bgcolor: alpha('#6366f1', 0.08),
                color: '#6366f1',
                borderRadius: 1,
              }}
            />
            {finding.forceMark && (
              <Chip
                label="Force Marked"
                size="small"
                sx={{ height: 20, fontSize: 10, bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }}
              />
            )}
            {finding.severity !== 'info' && (
              <Tooltip title={isTaskDisabled ? 'Task in progress' : 'Assign Task'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssignTask(finding.id);
                    }}
                    disabled={isTaskDisabled || finding.hasActiveTask}
                    sx={{
                      ml: 0.5,
                      color: '#8b5cf6',
                      '&:hover': { bgcolor: alpha('#8b5cf6', 0.1) },
                    }}
                  >
                    <Sparkles size={16} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
        <Divider sx={{ mb: 1.5 }} />

        {/* Meta row: severity, confidence, type, effort */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1.5}>
          <Chip
            icon={getSeverityIcon(finding.severity)}
            label={finding.severity.toUpperCase()}
            size="small"
            sx={{
              height: 22, fontSize: 11, fontWeight: 600,
              bgcolor: alpha(sevColor, 0.1), color: sevColor,
              '& .MuiChip-icon': { ml: 0.5 },
            }}
          />
          {finding.confidence && (
            <Tooltip title="Confidence level">
              <Chip
                icon={<Eye size={12} />}
                label={`${finding.confidence.toUpperCase()} confidence`}
                size="small"
                sx={{ height: 22, fontSize: 10, fontWeight: 500, bgcolor: alpha('#64748b', 0.08), color: '#64748b' }}
              />
            </Tooltip>
          )}
          {finding.effortMinutes && (
            <Tooltip title="Estimated fix effort">
              <Chip
                icon={<Clock size={12} />}
                label={formatEffort(finding.effortMinutes)}
                size="small"
                sx={{ height: 22, fontSize: 10, fontWeight: 500, bgcolor: alpha('#0ea5e9', 0.08), color: '#0ea5e9' }}
              />
            </Tooltip>
          )}
          {finding.forceMark && (
            <Tooltip title={`Marked by ${finding.forcedBy || 'user'} on ${finding.forcedAt ? new Date(finding.forcedAt).toLocaleString() : 'unknown'}: ${finding.forceJustification || ''}`}>
              <Chip label="Force Marked" size="small" sx={{ height: 20, fontSize: 10, bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }} />
            </Tooltip>
          )}
        </Stack>

        {/* CWE / OWASP tags */}
        {((Array.isArray(finding.cwe) && finding.cwe.length > 0) || (Array.isArray(finding.owasp) && finding.owasp.length > 0)) && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={1.5}>
            {Array.isArray(finding.cwe) && finding.cwe.map((c) => (
              <Chip key={c} label={c} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, fontFamily: 'monospace', bgcolor: alpha('#dc2626', 0.08), color: '#dc2626', borderRadius: 0.5 }} />
            ))}
            {Array.isArray(finding.owasp) && finding.owasp.map((o) => (
              <Chip key={o} label={o} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, fontFamily: 'monospace', bgcolor: alpha('#7c3aed', 0.08), color: '#7c3aed', borderRadius: 0.5 }} />
            ))}
          </Stack>
        )}

        {/* Description / Details */}
        {(finding.description || finding.details) && (
          <Box mb={1.5}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', mb: 0.25 }}>
              Description
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.primary', whiteSpace: 'pre-wrap' }}>
              {finding.description || finding.details}
            </Typography>
          </Box>
        )}

        {/* Evidence */}
        {finding.evidence && finding.evidence.length > 0 && (
          <Box mb={1.5}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              Evidence ({finding.evidence.length})
            </Typography>
            <Stack spacing={0.75}>
              {(showAllEvidence ? finding.evidence : finding.evidence.slice(0, EVIDENCE_CAP)).map((evidence, idx) => (
                <Paper
                  key={idx}
                  elevation={0}
                  sx={{
                    p: 1,
                    bgcolor: alpha('#000', 0.02),
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Code2 size={11} color={theme.palette.primary.main} />
                    <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: 'primary.main' }}>
                      {evidence.file}:{evidence.line_range}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                    {evidence.description}
                  </Typography>
                  {evidence.code_snippet && (
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 0.75,
                        bgcolor: alpha('#000', 0.04),
                        borderRadius: 0.5,
                        fontFamily: 'monospace',
                        fontSize: 10,
                        whiteSpace: 'pre-wrap',
                        overflow: 'auto',
                      }}
                    >
                      {evidence.code_snippet}
                    </Box>
                  )}
                </Paper>
              ))}
              {!showAllEvidence && finding.evidence.length > EVIDENCE_CAP && (
                <Button
                  size="small"
                  onClick={() => setShowAllEvidence(true)}
                  startIcon={<ChevronDown size={14} />}
                  sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, alignSelf: 'flex-start' }}
                >
                  Show all {finding.evidence.length} evidence items
                </Button>
              )}
            </Stack>
          </Box>
        )}

        {/* Recommendation */}
        {dedupedRecs.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'warning.main', mb: 0.5 }}>
              <Wrench size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Recommendation{dedupedRecs.length > 1 ? 's' : ''}
            </Typography>
            <Stack spacing={0.5}>
              {dedupedRecs.map((rec, idx) => (
                <Stack key={idx} direction="row" spacing={0.75} alignItems="flex-start">
                  <AlertTriangle size={12} color={theme.palette.warning.main} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, color: 'text.primary' }}>{rec}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
